import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { PersonUsesBook } from '@lyceo/lyceo-pupil-app/practise/util-models';
import { ScopeActions } from '../scope/scope.actions';
import { PersonUsesBookActions } from './person-uses-book.actions';
import { CallState, LoadingState } from '@lyceo/shared/util-loading';
import {
  mapPersonUsesBook,
  mapScopesToPersonUsesBooks,
} from './person-uses-book.functions';
import { SettingsActions } from '@lyceo/lyceo-pupil-app/user/data-access';
import { AuthActions } from '@lyceo/shared/auth/data-access';

export const PERSON_USES_BOOK_FEATURE_KEY: string = 'personUsesBook';

export interface PersonUsesBookState extends EntityState<PersonUsesBook> {
  latestCallState: CallState;
}

export const personUsesBookAdapter: EntityAdapter<PersonUsesBook> =
  createEntityAdapter<PersonUsesBook>();

export const initialPersonUsesBookState: PersonUsesBookState =
  personUsesBookAdapter.getInitialState({
    latestCallState: LoadingState.INIT,
  });

const reducer: ActionReducer<PersonUsesBookState, Action> = createReducer(
  initialPersonUsesBookState,
  on(
    AuthActions.logoutSuccess,
    (): PersonUsesBookState => initialPersonUsesBookState,
  ),
  on(
    ScopeActions.getPersonScopesSuccess,
    ScopeActions.switchBooksSuccess,
    (state, action): PersonUsesBookState =>
      personUsesBookAdapter.upsertMany(
        mapScopesToPersonUsesBooks(action.scopes),
        {
          ...state,
        },
      ),
  ),
  on(ScopeActions.switchBookSuccess, (state, action): PersonUsesBookState => {
    if (action.oldPersonUsesBook) {
      return personUsesBookAdapter.removeOne(action.oldPersonUsesBook.id, {
        ...state,
      });
    }
    return state;
  }),
  on(ScopeActions.switchBookSuccess, (state, action): PersonUsesBookState => {
    if (action.scope.personUsesBook) {
      return personUsesBookAdapter.upsertOne(
        mapPersonUsesBook(action.scope.personUsesBook, action.scope.id),
        {
          ...state,
        },
      );
    }
    return state;
  }),
  on(
    SettingsActions.updateLevelYearSuccess,
    (state): PersonUsesBookState =>
      personUsesBookAdapter.removeAll({
        ...state,
        latestCallState: LoadingState.INIT,
      }),
  ),
  on(
    PersonUsesBookActions.getLatestPersonUsesBooks,
    (state): PersonUsesBookState => ({
      ...state,
      latestCallState: LoadingState.LOADING,
    }),
  ),
  on(
    PersonUsesBookActions.getLatestPersonUsesBooksSuccess,
    (state, action): PersonUsesBookState =>
      personUsesBookAdapter.upsertMany(
        action.personUsesBooks.map((personUsesBook) =>
          mapPersonUsesBook(personUsesBook, personUsesBook.scope.id),
        ),
        {
          ...state,
          latestCallState: LoadingState.LOADED,
        },
      ),
  ),
  on(
    PersonUsesBookActions.getLatestPersonUsesBooksFailure,
    (state, { error }): PersonUsesBookState => ({
      ...state,
      latestCallState: { errorMsg: error.message },
    }),
  ),
);

export function personUsesBookReducer(
  state: PersonUsesBookState | undefined,
  action: Action,
): PersonUsesBookState {
  return reducer(state, action);
}
